// ScientificToNotationTask.js
// Zapisz w notacji wykładniczej: a · 10^k, gdzie a ∈ [1,10), k ∈ [-10,10].
// Wejście losujemy tak, by:
//  • dla liczb >1: całkowita z 1–5 cyfr znaczących + 0..(10-(sig-1)) zer (k ≤ 10),
//  • dla liczb <1: 0.(0…0)(1..5 cyfr znaczących), ostatnia cyfra ≠ 0 (k ≥ -10).
// Mantysa w odpowiedzi: 1–5 cyfr znaczących (max 4 po przecinku), BEZ zaokrąglania – ucinamy.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const int = (a,b)=> a + Math.floor(Math.random()*(b-a+1));
const pick = (arr)=> arr[Math.floor(Math.random()*arr.length)];
const stripSep = (s)=> String(s).replace(/\s/g,"").replace(",", ".");

// ---------- pomocnicze: budowa mantysy (bez zaokrąglania) ----------
function makeMantissaStr(digits, maxSig = 5){
  const ds = digits.replace(/^0+/, "") || "0";
  const sig = Math.max(1, Math.min(maxSig, ds.length));
  const first = ds[0];
  const rest = ds.slice(1, sig);
  let out = first + (rest ? "." + rest : "");
  out = out.replace(/0+$/,"").replace(/\.$/,"");
  return out;
}

function canonSciLatex(aStr, k){
  const aComma = aStr.replace(".", ",");
  return `${aComma}\\,\\cdot\\,10^{${k}}`;
}

// ---------- konwersja stringu liczby (z kropką) → {aStr, k} ----------
function toSciNoRound(xStr){
  let s = xStr;
  if (!s.includes(".")) s += ".0";
  s = s.replace(/^0+(?=\d)/,"");
  if (s.startsWith(".")) s = "0"+s;
  const [I,F] = s.split(".");

  if (I !== "0"){
    const digits = (I + F).replace(/^0+/,"");
    const k = I.length - 1;
    const aStr = makeMantissaStr(digits, 5);
    return { aStr, k };
  } else {
    const m = F.match(/^0*(\d)(\d*)$/);
    if (!m) return { aStr:"0", k:0 };
    const lead = m[1], rest = m[2] || "";
    const zeros = F.length - (rest.length + 1);
    const k = -(zeros + 1);
    const digits = lead + rest;
    const aStr = makeMantissaStr(digits, 5);
    return { aStr, k };
  }
}

// ---------- generator wejścia ----------
export function generateTask(opts = {}) {
  const preferSign =
    opts.preferSign === -1 || opts.preferSign === +1
      ? opts.preferSign
      : (Math.random() < 0.5 ? -1 : +1);

  let xStr = "0.0";

  if (preferSign === -1) {
    const zeros = int(0, 9);
    const sigLen = pick([1,2,3,4,5]);
    const head = int(1,9);
    let rest = "";
    if (sigLen > 1) {
      const midLen = sigLen - 2;
      if (midLen > 0) {
        const midMin = 10**(midLen-1), midMax = 10**midLen - 1;
        rest = String(int(midLen ? midMin : 0, midLen ? midMax : 0)).padStart(midLen, "0");
      }
      const last = int(1,9);
      rest = (midLen > 0 ? rest : "") + String(last);
    }
    xStr = `0.${"0".repeat(zeros)}${head}${rest}`;

  } else {
    const sigLen = pick([1,2,3,4,5]);
    const first = int(1,9);
    const tailLen = sigLen - 1;
    const tail = tailLen ? String(int(0, 10**tailLen - 1)).padStart(tailLen, "0") : "";
    const maxExtraZeros = 10 - (sigLen - 1);
    const extraZeros = int(0, Math.max(0, maxExtraZeros));
    const digits = String(first) + tail + "0".repeat(extraZeros);
    xStr = digits;
  }

  const { aStr, k } = toSciNoRound(xStr);
  if (k < -10 || k > 10) return generateTask(opts);
  const kSign = k < 0 ? -1 : (k > 0 ? +1 : 0);

  return {
    type: "ScientificToNotationTask",
    data: { xStr, aStr, k, kSign, sciLatex: canonSciLatex(aStr, k) }
  };
}

// ---------- UI ----------
export function renderTask({ data }) {
  const pretty = data.xStr.split(".").join(",");
  return (
    <div style={{ marginBottom: "12px" }}>
      Zapisz w <strong>notacji wykładniczej</strong>: <InlineMath math={pretty} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  const { aStr, k } = task.data;
  const ph=`np. 7,7·10^7 lub 7,7e7`;
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={ph}
        label=""
        powerPreview={false}
        sciPreview={true}  // ✅ jeden, poprawny podgląd notacji wykładniczej
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

// ---------- walidacja ----------
function parseSci(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g,"").replace(",",".");

  let m = s.match(/^(\d+(?:\.\d+)?)\*?·?10\^(-?\d+)$/) || s.match(/^(\d+(?:\.\d+)?)(?:\*|·)?10\^(-?\d+)$/);
  if (m) return { a: stripSep(m[1]), k: parseInt(m[2],10) };

  m = s.match(/^(\d+(?:\.\d+)?)[eE]([+\-]?\d+)$/);
  if (m) return { a: stripSep(m[1]), k: parseInt(m[2],10) };

  return null;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const p = parseSci(userAnswer.raw);
  if (!p) return false;
  if (p.k !== data.k) return false;

  const user = Number(p.a);
  const corr = Number(data.aStr);
  if (!(user >= 1 && user < 10)) return false;
  return Math.abs(user - corr) < 1e-12;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const p = parseSci(userAnswer.raw);
  if (!p) return <InlineMath math={`${String(userAnswer.raw).replace(".",",")}`} />;
  const aComma = p.a.replace(".", ",");
  return <InlineMath math={`${aComma}\\,\\cdot\\,10^{${p.k}}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.sciLatex} />;
}

export function renderExplanation({ data }) {
  const pretty = data.xStr.split(".").join(",");
  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Przesuń przecinek tak, aby uzyskać mantysę w <InlineMath math={'[1,10)'} />.</p>
      <BlockMath math={`${pretty}\\;=\\;${data.sciLatex}`} />
      <p><strong>Krok 2:</strong> Liczba przesunięć przecinka to wykładnik <InlineMath math={'k'} />.</p>
      <BlockMath math={`k=\\;${data.k}`} />
    </div>
  );
}
